import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Menu, Layout, Space, Avatar, Typography, Badge, Dropdown, 
  Drawer, Button, Grid, Divider, message
} from 'antd';
import {
  HomeOutlined,
  FolderOpenOutlined,
  EditOutlined,
  UserOutlined,
  BookOutlined,
  BellOutlined,
  SearchOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuOutlined,
  CloseOutlined,
  LoginOutlined,
  UserAddOutlined
} from '@ant-design/icons';
import './Navbar.css';

const { Header } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  
  // 监听滚动事件
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);
  
  // 获取当前登录用户
  const getCurrentUser = () => {
    const user = localStorage.getItem('blog_current_user');
    return user ? JSON.parse(user) : null;
  };

  // 检查是否已登录
  const isAuthenticated = () => {
    return !!localStorage.getItem('blog_current_user');
  };

  // 注销功能
  const handleLogout = (setMobileMenuVisible = null) => {
    localStorage.removeItem('blog_current_user');
    if (setMobileMenuVisible) {
      setMobileMenuVisible(false);
    }
    message.success('已成功退出登录');
    navigate('/auth');
  };

  // 获取当前选中的菜单项
  const getSelectedKeys = () => {
    const path = location.pathname;
    if (path === '/') return ['home'];
    if (path.startsWith('/archive')) return ['archive'];
    if (path.startsWith('/write')) return ['write'];
    if (path.startsWith('/profile')) return ['profile'];
    if (path.startsWith('/auth')) return ['auth'];
    if (path.startsWith('/search')) return ['search'];
    return ['home'];
  };

  const currentUser = getCurrentUser();
  const userName = currentUser ? currentUser.name || currentUser.username : 'zxj';
  const userInitial = userName.charAt(0).toUpperCase();

  // 用户下拉菜单项（根据登录状态显示不同菜单）
  const userMenuItems = isAuthenticated() ? [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: <Link to="/profile" onClick={() => setMobileMenuVisible && setMobileMenuVisible(false)}>个人中心</Link>,
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: <Link to="/settings" onClick={() => setMobileMenuVisible && setMobileMenuVisible(false)}>设置</Link>,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
      onClick: () => handleLogout(setMobileMenuVisible),
    },
  ] : [
    {
      key: 'login',
      icon: <LoginOutlined />,
      label: <Link to="/auth" onClick={() => setMobileMenuVisible && setMobileMenuVisible(false)}>登录</Link>,
    },
    {
      key: 'register',
      icon: <UserAddOutlined />,
      label: <Link to="/auth" state={{ tab: 'register' }} onClick={() => setMobileMenuVisible && setMobileMenuVisible(false)}>注册</Link>,
    }
  ];

  // 移动端菜单项
  const mobileMenuItems = [
    {
      key: 'home',
      icon: <HomeOutlined />,
      label: <Link to="/" onClick={() => setMobileMenuVisible(false)}>首页</Link>,
    },
    {
      key: 'archive',
      icon: <FolderOpenOutlined />,
      label: <Link to="/archive" onClick={() => setMobileMenuVisible(false)}>归档</Link>,
    },
    isAuthenticated() && {
      key: 'write',
      icon: <EditOutlined />,
      label: <Link to="/write" onClick={() => setMobileMenuVisible(false)}>写文章</Link>,
    },
    {
      key: 'search',
      icon: <SearchOutlined />,
      label: <Link to="/search" onClick={() => setMobileMenuVisible(false)}>搜索</Link>,
    },
    isAuthenticated() && {
      key: 'notifications',
      icon: <BellOutlined />,
      label: (
        <Link to="/notifications" onClick={() => setMobileMenuVisible(false)}>
          通知
          <Badge count={3} size="small" style={{ marginLeft: 8 }} />
        </Link>
      ),
    },
    isAuthenticated() && {
      key: 'profile',
      icon: <UserOutlined />,
      label: <Link to="/profile" onClick={() => setMobileMenuVisible(false)}>个人中心</Link>,
    },
    isAuthenticated() && {
      key: 'settings',
      icon: <SettingOutlined />,
      label: <Link to="/settings" onClick={() => setMobileMenuVisible(false)}>设置</Link>,
    },
    !isAuthenticated() && {
      key: 'auth',
      icon: <LoginOutlined />,
      label: <Link to="/auth" onClick={() => setMobileMenuVisible(false)}>登录/注册</Link>,
    },
    isAuthenticated() && {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
      onClick: () => handleLogout(setMobileMenuVisible),
    },
  ].filter(Boolean);

  // 移动端抽屉内容
  const mobileDrawerContent = () => {
    const user = getCurrentUser();
    return (
      <div className="mobile-menu-content">
        {user && (
          <div className="mobile-user-info">
            <Avatar
              size={64}
              src={user.avatar}
              icon={<UserOutlined />}
              style={{ 
                backgroundColor: user.avatarColor || '#1890ff',
                marginBottom: 16
              }}
            >
              {userInitial}
            </Avatar>
            <Text strong style={{ fontSize: '16px', display: 'block', marginBottom: 4 }}>
              {userName}
            </Text>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              {user.email || '前端开发者'}
            </Text>
          </div>
        )}
        
        <Divider style={{ margin: '16px 0' }} />
        
        <Menu
          mode="vertical"
          selectedKeys={getSelectedKeys()}
          items={mobileMenuItems}
          style={{ border: 'none' }}
        />
        
        {isAuthenticated() && (
          <div style={{ marginTop: 32, padding: '0 16px' }}>
            <Button 
              type="primary" 
              block 
              icon={<EditOutlined />}
              onClick={() => {
                setMobileMenuVisible(false);
                navigate('/write');
              }}
            >
              开始写作
            </Button>
          </div>
        )}
      </div>
    );
  };

  // 桌面端菜单项
  const desktopMenuItems = [
    {
      key: 'home',
      icon: <HomeOutlined />,
      label: <Link to="/">首页</Link>,
    },
    {
      key: 'archive',
      icon: <FolderOpenOutlined />,
      label: <Link to="/archive">归档</Link>,
    },
    isAuthenticated() && {
      key: 'write',
      icon: <EditOutlined />,
      label: <Link to="/write">写文章</Link>,
    },
    isAuthenticated() && {
      key: 'profile',
      icon: <UserOutlined />,
      label: <Link to="/profile">个人中心</Link>,
    },
  ].filter(Boolean);

  // 桌面端导航栏
  const renderDesktopNavbar = () => (
    <>
      {/* 左侧 Logo 和品牌 */}
      <div className="navbar-brand">
        <Link to="/" className="navbar-logo">
          <Space align="center" size={8}>
            <BookOutlined style={{ fontSize: '22px', color: '#1890ff' }} />
            <Text strong style={{ fontSize: '18px', color: '#1f2329' }}>
              我的技术博客
            </Text>
          </Space>
        </Link>
      </div>

      {/* 中间导航菜单 */}
      <div className="navbar-menu-container">
        <Menu
          mode="horizontal"
          selectedKeys={getSelectedKeys()}
          className="navbar-menu"
          items={desktopMenuItems}
        />
      </div>

      {/* 右侧操作区域 */}
      <div className="navbar-actions">
        <Space size="middle">
          {/* 搜索按钮 */}
          <Link to="/search" className="navbar-action-btn">
            <SearchOutlined style={{ fontSize: '18px', color: '#4e5969' }} />
          </Link>

          {/* 通知按钮 */}
          {isAuthenticated() && (
            <Badge count={3} size="small" offset={[-2, 2]}>
              <Link to="/notifications" className="navbar-action-btn">
                <BellOutlined style={{ fontSize: '18px', color: '#4e5969' }} />
              </Link>
            </Badge>
          )}

          {/* 用户头像和下拉菜单 */}
          <Dropdown
            menu={{
              items: userMenuItems,
            }}
            placement="bottomRight"
            trigger={['click']}
          >
            <div className="navbar-user">
              <Avatar
                size={36}
                src={currentUser?.avatar}
                icon={<UserOutlined />}
                style={{ 
                  backgroundColor: currentUser?.avatarColor || '#1890ff',
                  cursor: 'pointer',
                  border: '2px solid #fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                {userInitial}
              </Avatar>
              <Text style={{ marginLeft: 8, fontSize: '14px' }}>
                {userName}
              </Text>
            </div>
          </Dropdown>
        </Space>
      </div>
    </>
  );

  // 移动端导航栏
  const renderMobileNavbar = () => (
    <>
      <div className="navbar-brand">
        <Link to="/" className="navbar-logo">
          <Space align="center" size={8}>
            <BookOutlined style={{ fontSize: '22px', color: '#1890ff' }} />
            {screens.sm && (
              <Text strong style={{ fontSize: '18px', color: '#1f2329' }}>
                我的技术博客
              </Text>
            )}
          </Space>
        </Link>
      </div>

      <Space size="large">
        {/* 移动端通知图标 */}
        {isAuthenticated() && (
          <Badge count={3} size="small" offset={[-2, 2]}>
            <Link to="/notifications" className="navbar-action-btn">
              <BellOutlined style={{ fontSize: '20px', color: '#4e5969' }} />
            </Link>
          </Badge>
        )}

        {/* 汉堡菜单按钮 */}
        <Button
          type="text"
          icon={mobileMenuVisible ? <CloseOutlined /> : <MenuOutlined />}
          onClick={() => setMobileMenuVisible(!mobileMenuVisible)}
          style={{ 
            fontSize: '20px', 
            color: '#4e5969',
            padding: '4px 8px'
          }}
        />
      </Space>
    </>
  );

  return (
    <>
      <Header className={`navbar-header ${scrolled ? 'scrolled' : ''}`}>
        <div className="navbar-container">
          {screens.md ? renderDesktopNavbar() : renderMobileNavbar()}
        </div>
      </Header>

      {/* 移动端抽屉菜单 */}
      <Drawer
        title={
          <Space align="center">
            <BookOutlined style={{ color: '#1890ff' }} />
            <Text strong>我的技术博客</Text>
          </Space>
        }
        placement="right"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        // 修复: 使用 size 替代 width
        size={280}
        className="mobile-nav-drawer"
      >
        {mobileDrawerContent()}
      </Drawer>
    </>
  );
};

export default Navbar;